import { ImageModel, LinkModel, ModuleBaseModel, ModuleBaseNameProps, ModuleBasePropsModel } from '@/types';
import FullPageSlideMultiChildElement, { FullPageSlideMultiChildElementModel } from './FullPageSlideMultiChildElement';
import FullPageVideoSlideElement from './FullPageVideoSlideElement';
import FullPageSplitSlideElement, { FullPageVideoSlideElementModel } from './FullPageSplitSlideElement';
import FullPageSlideInfoBlockElement, { FullPageSlideInfoBlockElementModel } from './FullPageSlideInfoBlockElement';
import classNames from 'classnames';
import { SideBySideModuleModel } from '../SideBySideModule/SideBySideModule';

export type FullPageSideElementType = {
  moduleName: 'FullPageSideElement';
  props: {
    cta?: LinkModel;
    image?: ImageModel;
    text: string;
    theme: 'Light' | 'Dark' | '';
    title: string;
    titleHeading: string;
  };
};

export type FullPageSlideSlideType<T extends ModuleBaseNameProps> = {
  id: string;
  moduleName:
    | 'FullPageSlideMultiChildElement'
    | 'FullPageVideoSlideElement'
    | 'FullPageSplitSlideElement'
    | 'FullPageSlideInfoBlockElement';
  props: T;
};

type FullPageSlideModuleModel = ModuleBaseModel<
  {
    slides: FullPageSlideSlideType<ModuleBaseNameProps>[];
  } & ModuleBasePropsModel
>;

// https://examples.motion.dev/react/parallax
// https://codepen.io/pramith/pen/WNVqNrP
// https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-type
const FullPageSlideModule = (data: FullPageSlideModuleModel) => {
  const {
    props: { slides, anchor },
  } = data;

  const updatedSlides: FullPageSlideSlideType<ModuleBaseNameProps>[] = [];

  let skipNext = false;
  slides.forEach((slide, index) => {
    let pushed = false;
    if (skipNext) {
      skipNext = false;
      return;
    }
    const nextSlide = slides[index + 1];

    // merge info block slides to have the the common image
    if (
      nextSlide &&
      slide.moduleName === 'FullPageSlideMultiChildElement' &&
      nextSlide.moduleName === 'FullPageSlideMultiChildElement'
    ) {
      const slideModuleProps = slide.props as FullPageSlideMultiChildElementModel;
      const nextSlideModuleProps = nextSlide.props as FullPageSlideMultiChildElementModel;
      if (
        slideModuleProps.modules &&
        nextSlideModuleProps.modules &&
        slideModuleProps.modules.length === 1 &&
        nextSlideModuleProps.modules?.length === 1 &&
        slideModuleProps.modules[0].moduleName === 'SideBySideModule' &&
        nextSlideModuleProps.modules[0].moduleName === 'SideBySideModule'
      ) {
        const slideInfoBlock = slideModuleProps.modules[0] as SideBySideModuleModel;
        const nextSlideInfoBlock = nextSlideModuleProps.modules[0] as SideBySideModuleModel;
        if (
          slideInfoBlock.props.layout === 'Homepage Stats' &&
          nextSlideInfoBlock.props.layout === 'Homepage Create better futures'
        ) {
          skipNext = true;

          const infoBlockSlide: FullPageSlideSlideType<FullPageSlideInfoBlockElementModel> = {
            moduleName: 'FullPageSlideInfoBlockElement',
            id: slide.id,
            props: {
              modules: [slideInfoBlock, nextSlideInfoBlock],
              moduleName: 'FullPageSlideInfoBlockElement',
            },
          };

          updatedSlides.push(infoBlockSlide);
          pushed = true;
        }
      }
    }

    if (!pushed) {
      updatedSlides.push(slide);
    }
  });

  return (
    <div id={anchor}>
      {updatedSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={classNames(
            'relative flex flex-col items-center justify-center',
            slide.moduleName !== 'FullPageSlideInfoBlockElement' &&
              (index === 0
                ? 'h-[calc(100svh-var(--header-height))] md:h-screen md:snap-end [&_.module]:!snap-align-none'
                : 'md:h-[calc(100vh-var(--header-height))] md:snap-end [&_.module]:!snap-align-none'),
          )}
        >
          {slide.moduleName === 'FullPageSlideMultiChildElement' ? (
            <FullPageSlideMultiChildElement {...(slide.props as FullPageSlideMultiChildElementModel)} />
          ) : slide.moduleName === 'FullPageVideoSlideElement' ? (
            <FullPageVideoSlideElement {...(slide.props as FullPageVideoSlideElementModel)} />
          ) : slide.moduleName === 'FullPageSplitSlideElement' ? (
            <FullPageSplitSlideElement {...(slide.props as FullPageVideoSlideElementModel)} />
          ) : slide.moduleName === 'FullPageSlideInfoBlockElement' ? (
            <FullPageSlideInfoBlockElement {...(slide.props as FullPageSlideInfoBlockElementModel)} />
          ) : (
            <div>
              <strong>{slide.moduleName}</strong> not implemented
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default FullPageSlideModule;
